import { useTranslation, Trans } from "react-i18next";

import { useEffect } from "react";

import FormContainer from "@/pages/Public/components/FormContainer";

import { QuestionnaireImportance } from "../../enum/QuestionnaireImportance.enum";

interface NeedsAnalysisFinalResultsProps {
	isLoading: boolean;
	result: QuestionnaireImportance | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
	onStartSelfAssessment: () => void;
}

export default function NeedsAnalysisFinalResults({
	isLoading,
	result,
	formError,
	onStartSelfAssessment,
}: NeedsAnalysisFinalResultsProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) =>
		t(`questionnaire.needs-analysis.finalize.${key}`, options);
	const isNeededNIS2 = result === QuestionnaireImportance.IMPORTANT;

	const colorPallette = {
		[QuestionnaireImportance.IMPORTANT]: "#D9A400",
		[QuestionnaireImportance.NONE]: "#B8431A",
		[QuestionnaireImportance.STOP]: "#B8431A",
	};

	const showSelfAssessmentPrompt = result === QuestionnaireImportance.IMPORTANT;

	const showCybersecurityPrompt =
		result === QuestionnaireImportance.NONE || result === QuestionnaireImportance.STOP;

	const submitButtonText = isNeededNIS2 ? ts("start-self-assessment") : ts("check-cybersecurity");

	useEffect(() => {
		if (!result) {
			return;
		}

		const normalizedImportance =
			result === QuestionnaireImportance.STOP ? QuestionnaireImportance.IMPORTANT : result;

		localStorage.setItem("importance", normalizedImportance);
	}, [result]);

	return (
		<FormContainer
			changeActionsDirection
			actionContainerClassName="flex gap-2 !space-y-0"
			formContainerClassName="h-fit mt-16 mb-36"
			formError={formError}
			handleSubmit={onStartSelfAssessment}
			isLoading={isLoading}
			secondaryButtonType="button"
			showLogo={false}
			submitButtonText={submitButtonText}
			submitButtonType="button"
			title={ts("title")}
		>
			<div className="space-y-6 mt-6">
				<div>
					<h4 className="mb-3 text-[14px] font-semibold text-primary-blue-dark">
						{ts("overallComplianceLevel")}
						<span
							className="ml-2 py-1 px-2 rounded-md"
							style={{
								backgroundColor: colorPallette[result ?? QuestionnaireImportance.NONE] + "1F",
								color: colorPallette[result ?? QuestionnaireImportance.NONE],
							}}
						>
							{ts(result ?? "")}
						</span>
					</h4>

					{showSelfAssessmentPrompt && (
						<div className="text-[14px] text-primary-gray-lighter">
							<p>
								<Trans
									components={{ 1: <strong /> }}
									i18nKey="questionnaire.needs-analysis.finalize.self-assessment-text"
								/>
							</p>
						</div>
					)}

					{showCybersecurityPrompt && (
						<div className="text-[14px] text-primary-gray-lighter">
							<p>
								<Trans
									components={{ 1: <strong /> }}
									i18nKey="questionnaire.needs-analysis.finalize.cybersecurity-prompt-text"
								/>
							</p>
						</div>
					)}
				</div>
			</div>
		</FormContainer>
	);
}
